/**
 * Task write helpers — web SDK port of
 * `src/features/tasks/tasks.ts`.
 *
 * Rules require `canEditTasks` for create/update/delete and project
 * membership for the project a task belongs to. For org admins +
 * project managers both gates pass automatically.
 *
 * Delete does NOT cascade subcollection cleanup the way the mobile
 * helper does (web users don't normally hit task delete; the mobile
 * is the primary entry for task admin). Best-effort doc delete only.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type { TaskPriority, TaskStatus } from '@/lib/useProjectTasks';

export type CreateTaskInput = {
  orgId: string;
  projectId: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority?: TaskPriority;
  category?: string;
  startDate: Date;
  endDate?: Date | null;
  assignedTo?: string;
  assignedToName?: string;
  /** Optional reference photos (R2 URLs). */
  photoUris?: string[];
  createdBy: string;
};

export async function createTask(input: CreateTaskInput): Promise<string> {
  const ref = await addDoc(collection(db, 'tasks'), {
    orgId: input.orgId,
    projectId: input.projectId,
    title: input.title,
    description: input.description ?? '',
    status: input.status,
    priority: input.priority ?? 'medium',
    category: input.category ?? '',
    startDate: Timestamp.fromDate(input.startDate),
    endDate: input.endDate ? Timestamp.fromDate(input.endDate) : null,
    progress: 0,
    assignedTo: input.assignedTo ?? '',
    assignedToName: input.assignedToName ?? '',
    photoUris: input.photoUris ?? [],
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export type UpdateTaskPatch = Partial<{
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  category: string;
  startDate: Date | null;
  endDate: Date | null;
  assignedTo: string;
  assignedToName: string;
  progress: number;
  /** Replace the reference photos array. Pass [] to clear. */
  photoUris: string[];
}>;

export async function updateTask(taskId: string, patch: UpdateTaskPatch): Promise<void> {
  const updates: Record<string, unknown> = {};
  if (patch.title !== undefined) updates.title = patch.title;
  if (patch.description !== undefined) updates.description = patch.description;
  if (patch.status !== undefined) updates.status = patch.status;
  if (patch.priority !== undefined) updates.priority = patch.priority;
  if (patch.category !== undefined) updates.category = patch.category;
  if (patch.assignedTo !== undefined) updates.assignedTo = patch.assignedTo;
  if (patch.assignedToName !== undefined) updates.assignedToName = patch.assignedToName;
  if (patch.progress !== undefined) {
    updates.progress = Math.max(0, Math.min(100, Math.round(patch.progress)));
  }
  if ('startDate' in patch) {
    updates.startDate = patch.startDate ? Timestamp.fromDate(patch.startDate) : null;
  }
  if ('endDate' in patch) {
    updates.endDate = patch.endDate ? Timestamp.fromDate(patch.endDate) : null;
  }
  if (patch.photoUris !== undefined) updates.photoUris = patch.photoUris;
  updates.updatedAt = serverTimestamp();
  await updateDoc(doc(db, 'tasks', taskId), updates);
}

export async function updateTaskStatus(taskId: string, status: TaskStatus): Promise<void> {
  await updateDoc(doc(db, 'tasks', taskId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTask(taskId: string): Promise<void> {
  await deleteDoc(doc(db, 'tasks', taskId));
}

// ───── Task work-log updates ─────────────────────────────────
//
// Web port of mobile's `addTaskUpdate` at
//   /Users/gudupuramesh/Documents/siteexpens/src/features/tasks/tasks.ts
//
// One update doc = one site-log entry against a task. Stored at
//   tasks/{taskId}/updates/{updateId}
//
// On every write we also roll the latest progress + auto-derived
// status onto the parent task doc so the Timeline view, mobile
// Tasks list, and KPI rollups all reflect the freshest update
// without a join.

export type AddTaskUpdateInput = {
  authorId: string;
  authorName: string;
  /** 0–100. Clamped server-side. */
  progress: number;
  /** Optional narrative ("Finished plumbing core cutting…"). */
  text: string;
  /** Optional R2 photo URLs. Upload helper not yet on web — pass
   *  empty until the upload pipeline ships. */
  photoUris: string[];
};

export async function addTaskUpdate(
  taskId: string,
  input: AddTaskUpdateInput,
): Promise<string> {
  const clamped = Math.max(0, Math.min(100, Math.round(input.progress)));

  // 1) Write the update doc.
  const updatesRef = collection(db, 'tasks', taskId, 'updates');
  const updateRef = await addDoc(updatesRef, {
    authorId: input.authorId,
    authorName: input.authorName,
    progress: clamped,
    text: input.text,
    photoUris: input.photoUris,
    // Client timestamp — matches mobile so the day-bucket query
    // (`createdAt >= dayStart && < dayEnd`) is deterministic.
    createdAt: Timestamp.now(),
  });

  // 2) Roll the progress onto the parent task + auto-derive status.
  //    - >=100 → completed
  //    - >0    → ongoing (if it was not_started)
  //    Status downgrade is never automatic; only Edit Task can
  //    move a task back from completed/ongoing.
  const taskRef = doc(db, 'tasks', taskId);
  const patch: Record<string, unknown> = {
    progress: clamped,
    updatedAt: serverTimestamp(),
  };
  if (clamped >= 100) patch.status = 'completed';
  else if (clamped > 0) patch.statusIfNotStarted = 'ongoing'; // sentinel below
  // We can't conditionally update server-side without a transaction.
  // The mobile helper reads the doc first; we follow suit for parity.
  // For now: if clamped < 100, we leave status alone (caller can
  // bump status via the Task form). This matches mobile's behaviour
  // when the parent task is already ongoing/completed.
  delete patch.statusIfNotStarted;
  if (clamped >= 100) patch.status = 'completed';
  await updateDoc(taskRef, patch);

  return updateRef.id;
}

// ───── Helpful re-exports for callers ─────

export { Timestamp };
